import {inputBaseClasses} from '@mui/material';
import InputAdornment, {inputAdornmentClasses} from '@mui/material/InputAdornment';
import {styled} from '@mui/material/styles';
import MuiTextField, {TextFieldProps as MuiTextFieldProps} from '@mui/material/TextField';
import {uniqueId} from 'lodash/fp';
import {ComponentType, useState} from 'react';
import {Merge} from 'type-fest';

import {styleVariables} from 'core/constants';

import CheckIcon from 'components/icons/outlinedCheck.svg';

export {inputBaseClasses};

export type TextFieldProps = Merge<
  MuiTextFieldProps,
  {
    /**
     * prevents input layout height from jumping on validation
     * text appearing by reserving dummy space for helper text
     */
    helperTextRequired?: boolean;
    /**
     * Mark filled field by checked icon
     */
    withCheckedIcon?: boolean;
    renderInput?: ComponentType<MuiTextFieldProps>;
  }
> &
  DataTestId;

const SInputAdornment = styled(InputAdornment)`
  margin-left: 10px;

  &.${inputAdornmentClasses.positionEnd} {
    color: ${styleVariables.colorPalette.electricTeal(600)};
  }
`;

const TextField = ({
  id,
  autoComplete = 'off',
  className: _,
  autoFocus = false,
  disabled = false,
  error = false,
  fullWidth = false,
  multiline = false,
  required = false,
  placeholder,
  renderInput: TextFieldComponent = MuiTextField,
  inputRef,
  helperText,
  helperTextRequired,
  withCheckedIcon,
  dataTestId,
  slotProps = {},
  ...props
}: TextFieldProps) => {
  const [inputName] = useState(id || uniqueId('text-field-'));
  const helperTextOrDummy = helperTextRequired ? helperText || <span>&nbsp;</span> : helperText;
  const withHelperText = Boolean(helperText || helperTextRequired);

  return (
    <TextFieldComponent
      autoFocus={autoFocus}
      fullWidth={fullWidth}
      multiline={multiline}
      inputRef={inputRef}
      id={`${inputName}-input`}
      aria-describedby={`${inputName}-helper-text`}
      aria-labelledby={`${inputName}-label`}
      autoComplete={autoComplete}
      error={error}
      required={required}
      disabled={disabled}
      placeholder={placeholder}
      helperText={withHelperText ? helperTextOrDummy : undefined}
      data-testid={dataTestId}
      slotProps={{
        ...slotProps,
        input: {
          endAdornment: withCheckedIcon && (
            <SInputAdornment position='end'>
              <CheckIcon width={20} height={20} />
            </SInputAdornment>
          ),
          ...slotProps.input,
        },
        // @ts-ignore input placeholder hidden on data-shrink={false}
        inputLabel: {
          ...slotProps.inputLabel,
          ...(placeholder ? {'data-shrink': true} : {}),
        },
      }}
      {...props}
    />
  );
};

export default TextField;
